import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { interval, Subscription, takeWhile } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { LoaderService } from '../../Shared Utilities/global-loader/loader.service';
import { UserLoginService } from '../userlogin.service';
import {
  AuthenticationRequest,
  UserLoginAuthResponseDTO,
  UserLoginGenerateOtpRequestDTO,
  UserLoginGenerateOtpResponseDTO,
  UserLoginValidateOtpRequestDTO
} from '../userlogin.interface';

@Component({
  selector: 'app-hospital-login',
  templateUrl: './hospital-login.component.html',
  styleUrls: ['./hospital-login.component.css']
})
export class HospitalLoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  mobileForm: FormGroup;
  otpForm: FormGroup;

  isMobileLogin = false;
  isOtpSent = false;
  maskedMessage = '';
  showPassword = false;

  // timer
  timerSeconds = 0;
  private timerSub: Subscription | null = null;
  readonly OTP_TIMEOUT_SECONDS = 60;

  loading = false;
  resendOtpDisabled = true;

  roles = [
    { value: 'admin', label: 'Administrator', icon: 'fas fa-user-shield' },
    { value: 'doctor', label: 'Doctor / Medical Staff', icon: 'fas fa-user-md' },
    { value: 'patient', label: 'Patient Portal', icon: 'fas fa-user-injured' }
  ];
  selectedRole = 'doctor';

  constructor(
    private fb: FormBuilder,
    private userLoginService: UserLoginService,
    private router: Router,
    private loader: LoaderService,
    private toastr: ToastrService
  ) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      role: ['doctor', Validators.required]
    });

    this.mobileForm = this.fb.group({
      mobileNo: ['', [Validators.required, Validators.pattern(/^[6-9]\d{9}$/)]]
    });

    this.otpForm = this.fb.group({
      otp1: ['', Validators.required],
      otp2: ['', Validators.required],
      otp3: ['', Validators.required],
      otp4: ['', Validators.required],
      otp5: ['', Validators.required],
      otp6: ['', Validators.required],
      otp: ['']
    });
  }

  ngOnInit(): void {
    if (this.userLoginService.isAuthenticated()) {
      this.router.navigate(['/']);
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  onLogin() {
    this.loginForm.markAllAsTouched();
    if (this.loginForm.valid) {
      this.loader.show();
      const userData: AuthenticationRequest = {
        userName: this.loginForm.value.username,
        password: this.loginForm.value.password
      };

      this.userLoginService.UserLogin_Auth(userData).subscribe({
        next: (res: UserLoginAuthResponseDTO) => {
          this.loader.dismiss();
          const token = res.data.token?.trim();
          if (token) {
            localStorage.setItem('access_token', token);
            localStorage.setItem('user_role', this.loginForm.value.role);

            if (res.statusCode == 403) {
              this.router.navigate(['/reset-password', res.data.userId]);
            } else {
              this.router.navigate(['/']);
            }
          } else {
            this.toastr.error('Login response did not contain a token.');
          }
        },
        error: (err) => {
          this.loader.dismiss();
          this.toastr.error('Login failed. Please check your credentials.');
        }
      });
    }
  }

  sendOtp(): void {
    this.mobileForm.markAllAsTouched();
    if (this.mobileForm.invalid) return;

    this.loading = true;
    const payLoad: UserLoginGenerateOtpRequestDTO = {
      mobileNo: this.mobileForm.value.mobileNo
    };

    this.userLoginService.UserLogin_LoginGenerateOtp(payLoad).subscribe({
      next: (res: UserLoginGenerateOtpResponseDTO) => {
        this.loading = false;
        if (res.statusCode === 200) {
          this.isOtpSent = true;
          this.maskedMessage = res.message;
          this.startTimerFromExpiry(res.expiryTime);
          setTimeout(() => {
            this.resendOtpDisabled = false;
          }, 90000);
        } else {
          this.toastr.warning(res.message);
        }
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error('Failed to send OTP. Please try again.');
      }
    });
  }

  verifyOtp(): void {
    this.otpForm.markAllAsTouched();
    if (this.otpForm.invalid) return;

    const payLoad: UserLoginValidateOtpRequestDTO = {
      mobileNo: this.mobileForm.value.mobileNo,
      otp: this.otpForm.value.otp
    };

    this.loading = true;
    this.userLoginService.UserLogin_LoginValidateOtp(payLoad).subscribe({
      next: (res) => {
        this.loading = false;
        if (res && res.statusCode === 200) {
          const token = res.data?.token;
          if (token) {
            localStorage.setItem('access_token', token.trim());
            this.router.navigate(['/']);
          } else {
            this.toastr.error('OTP verification succeeded but no token received.');
          }
        } else {
          this.toastr.error('Invalid OTP. Please try again.');
        }
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error('OTP verification failed.');
      }
    });
  }

  startTimerFromExpiry(expiryTime: any): void {
    this.clearTimer();
    const expiry = new Date(expiryTime).getTime();
    const now = Date.now();
    const diff = Math.floor((expiry - now) / 1000);
    this.timerSeconds = diff > 0 ? diff : 0;
    if (this.timerSeconds <= 0) return;

    this.timerSub = interval(1000)
      .pipe(takeWhile(() => this.timerSeconds > 0))
      .subscribe(() => {
        this.timerSeconds--;
        if (this.timerSeconds === 0) this.clearTimer();
      });
  }

  clearTimer(): void {
    if (this.timerSub) {
      this.timerSub.unsubscribe();
      this.timerSub = null;
    }
    this.timerSeconds = 0;
  }

  resendOtp(): void {
    this.sendOtp();
  }

  toggleLoginMode(): void {
    this.isMobileLogin = !this.isMobileLogin;
    this.loginForm.reset({ role: 'doctor' });
    this.mobileForm.reset();
    this.otpForm.reset();
    this.isOtpSent = false;
    this.maskedMessage = '';
    this.resendOtpDisabled = true;
    this.clearTimer();
  }

  changeMobileNo(): void {
    this.isOtpSent = false;
    this.maskedMessage = '';
    this.otpForm.reset();
    this.clearTimer();
  }

  onOtpInput(event: any, index: number) {
    const value = event.target.value;
    if (value.length === 1 && index < 6) {
      const next = document.getElementById('otp' + (index + 1));
      next?.focus();
    }
    if (value.length === 0 && index > 1) {
      const prev = document.getElementById('otp' + (index - 1));
      prev?.focus();
    }
    let finalOtp = '';
    for (let i = 1; i <= 6; i++) {
      finalOtp += this.otpForm.get('otp' + i)?.value || '';
    }
    this.otpForm.patchValue({ otp: finalOtp });
  }
}
